import { Project, SubProject, SubProjectResource } from '../../../models/interfaces';
import { Box, Chip, IconButton, Paper, Stack, Switch, Table, TableBody, TableCell, TableFooter, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { calculateProjectCost, calculateResourceCost } from '../project-utils';
import { ProjectAddResource } from '../project-add/project-add-resource';
import LayersIcon from '@mui/icons-material/Layers'
import { useSelector } from 'react-redux';
import { selectAllRates } from '../../../slices/rates';

const ODD_OPACITY = 0.2;
const tableStyles = {
    stickyFooter: {
        position: 'sticky',
        bottom: 0,
        backgroundColor: 'background.paper',
        borderTop: '2px solid #e0e0e0',
    } as React.CSSProperties,
    cellMaterial: {
        '&:hover': {
            backgroundColor: 'lightgray', // Change background color on hover
        },
    } as React.CSSProperties,
};

export const ProjectSummaryCostingTableResourceRows = (props: { project: Project, sub_project_colors: Record<string, string>, setSummaryType: any, columns: number }) => {
    const rates = useSelector(selectAllRates);

    const [resource_amount_ex_vat, setResourceAmountExVat] = useState(0);
    const [resource_amount_incl_vat, setResourceAmountInclVat] = useState(0);
    const [resource_percentage_of_category, setResourcePercentageOfCategory] = useState(0);
    const [resource_amount_ex_vat_levy, setResourceAmountExVatLevy] = useState(0);
    const [resource_amount_incl_vat_levy, setResourceAmountInclVatLevy] = useState(0);
    const [resource_percentage_of_category_levy, setResourcePercentageOfCategoryLevy] = useState(0);
    const [resource_amount_ex_vat_total, setResourceAmountExVatTotal] = useState(0);
    const [resource_amount_incl_vat_total, setResourceAmountInclVatTotal] = useState(0);
    const [resource_percentage_of_total, setResourcePercentageOfTotal] = useState(0);

    useEffect(() => {
        const resource_cost: number = calculateResourceCost(props.project);
        setResourceAmountExVat(resource_cost);
        setResourceAmountInclVat(resource_cost * 1.15);
        setResourceAmountExVatLevy(resource_cost * 0.1);
        setResourceAmountInclVatLevy(resource_cost * 0.1 * 1.15);
        setResourceAmountExVatTotal(resource_cost * 1.1);
        setResourceAmountInclVatTotal(resource_cost * 1.1 * 1.15);
        setResourcePercentageOfCategory((resource_cost) / (resource_cost * 1.1) * 100 || 0);
        setResourcePercentageOfCategoryLevy((resource_cost * 0.1) / (resource_cost * 1.1) * 100 || 0);
        setResourcePercentageOfTotal(resource_cost * 1.1 / calculateProjectCost(props.project, rates) * 100 || 0)
    }, [props.project])

    const handleCellContextMenu = (e: React.MouseEvent<HTMLTableCellElement, MouseEvent>, text: string) => {
        e.stopPropagation();
        e.preventDefault();
        if (text) {
            navigator.clipboard.writeText(text);
        }
    }

    return (
        <>
            <TableRow key={'material-heading'}>
                <TableCell colSpan={props.columns}>
                    <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} sx={{ pl: 1, pt: 2, pb: 0 }} spacing={2}>
                        <Stack direction={'row'} alignItems={'center'} spacing={1}>
                            <IconButton size='small' onClick={() => props.setSummaryType('material')}>
                                <LayersIcon />
                            </IconButton>
                            <Typography variant='h6' fontWeight={'bold'} >
                                Material
                            </Typography>
                        </Stack>
                        <Stack direction={'row'} alignItems={'center'} spacing={1}>
                            <Tooltip title={`This project has ${props.project.sub_projects.reduce((length, sub_project) => { return length + sub_project.sub_project_resources.length }, 0)} resources`}>
                                <Chip
                                    sx={{ pl: 0.5 }}
                                    label={`${props.project.sub_projects.reduce((length, sub_project) => { return length + sub_project.sub_project_resources.length }, 0)}`}
                                    size='small'
                                    variant='outlined'
                                    color='default'
                                    icon={<LayersIcon />}
                                />
                            </Tooltip>
                            <ProjectAddResource project={props.project} sub_project_colors={props.sub_project_colors} />
                        </Stack>
                    </Stack>
                </TableCell>
            </TableRow>
            {/* Header */}
            <TableRow key={'material-header'}>
                <TableCell colSpan={props.columns - 4}>
                    {/* empty cell */}
                </TableCell>
                <TableCell align='right' onContextMenu={(e) => handleCellContextMenu(e, 'Amount Ex. VAT [R]')}>
                    <Typography variant='caption' fontWeight={'bold'}>
                        Amount Ex. VAT [R]
                    </Typography>
                </TableCell>
                <TableCell align='right' onContextMenu={(e) => handleCellContextMenu(e, 'Amount Incl. VAT [R]')}>
                    <Typography variant='caption' fontWeight={'bold'}>
                        Amount Incl. VAT [R]
                    </Typography>
                </TableCell>
                <TableCell align='right' onContextMenu={(e) => handleCellContextMenu(e, '[%] of Category')}>
                    <Typography variant='caption' fontWeight={'bold'}>
                        [%] of Category
                    </Typography>
                </TableCell>
                <TableCell align='right' onContextMenu={(e) => handleCellContextMenu(e, '[%] of Total')}>
                    <Typography variant='caption' fontWeight={'bold'}>
                        [%] of Total
                    </Typography>
                </TableCell>
            </TableRow>
            {/* Body */}
            <TableRow key={'material-subtotal'} hover>
                <TableCell colSpan={props.columns - 4} onContextMenu={(e) => handleCellContextMenu(e, 'Sub - Total Material')}>
                    Sub - Total Material
                </TableCell>
                <TableCell align='right' onContextMenu={(e) => handleCellContextMenu(e, (resource_amount_ex_vat).toFixed(2))}>
                    {(resource_amount_ex_vat).toFixed(2)}
                </TableCell>
                <TableCell align='right' onContextMenu={(e) => handleCellContextMenu(e, (resource_amount_incl_vat).toFixed(2))}>
                    {(resource_amount_incl_vat).toFixed(2)}
                </TableCell>
                <TableCell align='right' onContextMenu={(e) => handleCellContextMenu(e, (resource_percentage_of_category).toFixed(2))}>
                    {(resource_percentage_of_category).toFixed(2)}
                </TableCell>
                <TableCell align='right'>
                    {/* empty cell */}
                </TableCell>
            </TableRow>
            <TableRow key={'material-levy'} hover>
                <TableCell colSpan={props.columns - 4} onContextMenu={(e) => handleCellContextMenu(e, '10% Material Levy')}>
                    10% Material Levy
                </TableCell>
                <TableCell align='right' onContextMenu={(e) => handleCellContextMenu(e, (resource_amount_ex_vat_levy).toFixed(2))}>
                    {(resource_amount_ex_vat_levy).toFixed(2)}
                </TableCell>
                <TableCell align='right' onContextMenu={(e) => handleCellContextMenu(e, (resource_amount_incl_vat_levy).toFixed(2))}>
                    {(resource_amount_incl_vat_levy).toFixed(2)}
                </TableCell>
                <TableCell align='right' onContextMenu={(e) => handleCellContextMenu(e, (resource_percentage_of_category_levy).toFixed(2))}>
                    {(resource_percentage_of_category_levy).toFixed(2)}
                </TableCell>
                <TableCell align='right'>
                    {/* empty cell */}
                </TableCell>
            </TableRow>
            {/* Footer */}
            <TableRow key={'material-footer'} hover>
                <TableCell colSpan={props.columns - 4} onContextMenu={(e) => handleCellContextMenu(e, 'Total material')}>
                    <Typography variant='caption' fontWeight={'bold'}>
                        Total material
                    </Typography>
                </TableCell>
                <TableCell align='right' onContextMenu={(e) => handleCellContextMenu(e, (resource_amount_ex_vat_total).toFixed(2))}>
                    <Typography variant='caption' fontWeight={'bold'}>
                        {(resource_amount_ex_vat_total).toFixed(2)}
                    </Typography>
                </TableCell>
                <TableCell align='right' onContextMenu={(e) => handleCellContextMenu(e, (resource_amount_incl_vat_total).toFixed(2))}>
                    <Typography variant='caption' fontWeight={'bold'}>
                        {(resource_amount_incl_vat_total).toFixed(2)}
                    </Typography>
                </TableCell>
                <TableCell align='right'>
                    {/* empty cell */}
                </TableCell>
                <TableCell align='right' onContextMenu={(e) => handleCellContextMenu(e, (resource_percentage_of_total).toFixed(2))}>
                    <Typography variant='caption' fontWeight={'bold'}>
                        {(resource_percentage_of_total).toFixed(2)}
                    </Typography>
                </TableCell>
            </TableRow>
        </>
    );
}